import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './NotificationIcon.css';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import { useNotifications, Notification } from './NotificationContext';
import { useNavigate } from 'react-router-dom';
import DeleteModal from '../../../main/components/modal/delete/DeleteModal';
import { translateNotificationContent } from '../../notifications/notificationTranslation';

interface NotificationIconProps {
  className?: string;
  iconSize?: number;
}

const NotificationIcon: React.FC<NotificationIconProps> = ({ className = '', iconSize = 24 }) => {
  const { i18n } = useTranslation();
  const currentLocale = i18n.resolvedLanguage || i18n.language || 'al';
  const { notifications, unreadCount, fetchNotifications, markAsRead, markAllAsRead, clearAll } = useNotifications();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);
  const [viewportTick, setViewportTick] = useState(0);
  const navigate = useNavigate();
  const dropdownStyle = useMemo(() => {
    if (!anchorEl) return undefined;

    const rect = anchorEl.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const isMobile = viewportWidth <= 768;

    if (isMobile) {
      return {
        top: 16,
        left: 16,
        right: 16,
        width: 'auto',
        maxHeight: 'calc(100vh - 32px)',
      } as React.CSSProperties;
    }

    const dropdownWidth = Math.min(380, viewportWidth - 28);
    const preferredLeft = rect.right - dropdownWidth;
    const safeLeft = Math.max(14, Math.min(preferredLeft, viewportWidth - dropdownWidth - 14));

    return {
      top: rect.bottom + 10,
      left: safeLeft,
      width: dropdownWidth,
    } as React.CSSProperties;
  }, [anchorEl, isOpen, viewportTick]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleViewportChange = () => {
      setViewportTick((current) => current + 1);
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [anchorEl, isOpen]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setIsOpen(true);
    void fetchNotifications(false, 1, 10);
  };

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => setAnchorEl(null), 300);
  };

  const handleNotificationClick = (notification: Notification) => {
    void markAsRead(notification.id);
    handleClose();
  };

  const handleMarkAllAsRead = () => {
    void markAllAsRead();
  };

  const handleClearAll = () => {
    setIsClearConfirmOpen(true);
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return i18n.t('mainLanding.notifications.now', { defaultValue: 'Now' });
    if (diffMins < 60) return i18n.t('mainLanding.notifications.minutesAgo', { defaultValue: '{{count}} min', count: diffMins });
    if (diffHours < 24) return i18n.t('mainLanding.notifications.hoursAgo', { defaultValue: '{{count}} hr', count: diffHours });
    if (diffDays < 7) return i18n.t('mainLanding.notifications.daysAgo', { defaultValue: '{{count}} days', count: diffDays });

    return date.toLocaleDateString(currentLocale, { day: 'numeric', month: 'short' });
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'like':
        return 'L';
      case 'comment':
        return 'C';
      case 'mention':
        return 'M';
      case 'success':
        return 'S';
      case 'warning':
        return 'W';
      case 'error':
        return 'E';
      default:
        return 'N';
    }
  };

  const getNotificationToneClass = (type: Notification['type']) => {
    switch (type) {
      case 'success':
        return 'isSuccess';
      case 'warning':
        return 'isWarning';
      case 'error':
        return 'isError';
      default:
        return 'isInfo';
    }
  };

  return (
    <div className={`notification-container ${className}`}>
      <button className="notification-trigger" onClick={handleOpen}>
        {unreadCount > 0 ? (
          <div className="notification-badge-wrapper">
            <NotificationsIcon className="notification-icon" style={{ fontSize: iconSize }} />
            <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
          </div>
        ) : (
          <NotificationsNoneIcon className="notification-icon" style={{ fontSize: iconSize }} />
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown" style={dropdownStyle}>
          <div className="notification-header">
            <div className="notification-header-top">
              <h3 className="notification-title">{i18n.t('mainLanding.notifications.title', { defaultValue: 'Notifications' })}</h3>
              <button
                type="button"
                className="notification-close-btn"
                onClick={handleClose}
                aria-label={i18n.t('mainLanding.actions.close', { defaultValue: 'Close' })}
                title={i18n.t('mainLanding.actions.close', { defaultValue: 'Close' })}
              >
                <CloseIcon fontSize="small" />
              </button>
            </div>
            <div className="notification-actions">
              {unreadCount > 0 && (
                <button className="action-btn mark-all-btn" onClick={handleMarkAllAsRead}>
                  <CheckCircleIcon fontSize="small" />
                  {i18n.t('mainLanding.notifications.markAllRead', { defaultValue: 'Mark all as read' })}
                </button>
              )}
              {notifications.length > 0 && (
                <button className="action-btn clear-all-btn" onClick={handleClearAll}>
                  <DeleteIcon fontSize="small" />
                  {i18n.t('mainLanding.notifications.clearPanel', { defaultValue: 'Clear panel' })}
                </button>
              )}
            </div>
          </div>

          <div className="notification-list">
            {notifications.length > 0 ? (
              notifications.map((notification) => {
                const translatedNotification = translateNotificationContent(notification, i18n);
                return (
                  <div
                    key={notification.id}
                    className={`notification-item ${notification.unread ? 'unread' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notification-icon-container">
                      <div className={`notification-type-icon ${getNotificationToneClass(notification.type)}`}>
                        <span>{getNotificationIcon(notification.type)}</span>
                      </div>
                    </div>

                    <div className="notification-content">
                      {translatedNotification.title && <div className="notification-message">{translatedNotification.title}</div>}
                      <div className="notification-message">{translatedNotification.message}</div>
                      <div className="notification-meta">
                        <span className="notification-time">{getTimeAgo(notification.createdAt)}</span>
                        {notification.fromUser && <span className="notification-user">@{notification.fromUser}</span>}
                      </div>
                    </div>

                    {notification.unread && <div className="unread-indicator"></div>}
                  </div>
                );
              })
            ) : (
              <div className="no-notifications">
                <div className="empty-icon">N</div>
                <p className="empty-message">{i18n.t('mainLanding.notifications.emptyTitle', { defaultValue: 'No new notifications' })}</p>
                <p className="empty-subtitle">{i18n.t('mainLanding.notifications.emptySubtitle', { defaultValue: 'Come back later for updates' })}</p>
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="notification-footer">
              <button className="view-all-btn" onClick={() => navigate('/notifications')}>
                {i18n.t('mainLanding.notifications.viewAll', { defaultValue: 'View all notifications' })}
              </button>
            </div>
          )}
        </div>
      )}

      {isOpen && <div className="notification-overlay" onClick={handleClose} />}

      {isClearConfirmOpen && (
        <DeleteModal
          width="460px"
          handleClose={() => setIsClearConfirmOpen(false)}
          handleDelete={() => {
            clearAll();
            setIsClearConfirmOpen(false);
          }}
        >
          {i18n.t('mainLanding.notifications.clearConfirm', { defaultValue: 'Remove all loaded notifications from this panel?' })}
        </DeleteModal>
      )}
    </div>
  );
};

export default NotificationIcon;
